import { TableCell, TableRow } from '@mui/material';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import ArtworkCol from 'commons/ArtworkCol';
import useRole from 'hooks/useRole';
import CertificateDialog from 'pages/ArtworkDetail/components/CertificateDialog';
import { formatDateString, getTextEdition } from 'utils/utils';

import TableCertificateAction from '../TableCertificateAction';
import useStyles from './styles';

const CertificateRow = ({ certificate, index, page, pageSize, highlightCertificateCode }) => {
  const classes = useStyles();
  const [isShowCertificateView, setIsShowCertificateView] = useState(false);
  const [autoOpenDownload, setAutoOpenDownload] = useState(false);
  const rowRef = useRef(null);
  const didAutoOpenRef = useRef(false);

  const { isArtist } = useRole();
  const currentUserId = useSelector(
    (state) => state.auth?.user?.id ?? state.auth?.account?.user?.id,
  );

  const displayIndex = (page - 1) * pageSize + (index + 1);
  const formattedIssuedDate = formatDateString(certificate?.created_at);
  const formattedUpdateDate = formatDateString(certificate?.updated_at);

  const handleCancelCertificateView = () => {
    setIsShowCertificateView(false);
    setAutoOpenDownload(false);
  };

  const isHighlighted =
    !!highlightCertificateCode && String(certificate?.code) === String(highlightCertificateCode);

  useEffect(() => {
    if (!isHighlighted) {
      return;
    }
    rowRef.current?.scrollIntoView?.({ block: 'center' });
    if (didAutoOpenRef.current) {
      return;
    }
    didAutoOpenRef.current = true;
    setIsShowCertificateView(true);
  }, [isHighlighted]);

  // Synthesize an artwork object with a single-edition editions array so that
  // useOwnedEditions (inside Certificate) can identify the user's owned edition.
  const artworkWithEdition = useMemo(() => {
    const artwork = certificate?.artwork;
    if (!artwork || !certificate || !currentUserId) return artwork;
    return {
      ...artwork,
      is_owner: true,
      editions: [
        {
          id: certificate.id,
          edition_number: certificate.edition_number,
          id_certificate: certificate.id,
          owner_certificate: { id: currentUserId },
          total_edition: artwork?.total_edition,
        },
      ],
    };
  }, [certificate, currentUserId]);

  return (
    <>
      <TableRow
        key={index}
        ref={rowRef}
        className={clsx(classes.tableRowBorder, { [classes.tableRowHighlight]: isHighlighted })}
      >
        <TableCell>{displayIndex}</TableCell>

        <TableCell>
          <ArtworkCol
            id={certificate?.artwork?.id}
            image={certificate?.artwork?.image}
            title={certificate?.artwork?.title}
            isLink={false}
          />
        </TableCell>
        <TableCell>
          {getTextEdition(certificate?.edition_number, certificate?.artwork?.total_edition)}
        </TableCell>
        <TableCell>
          {isArtist ? certificate?.issued_to?.name : certificate?.issued_by?.name}
        </TableCell>
        <TableCell>{certificate?.code}</TableCell>
        <TableCell>{formattedIssuedDate}</TableCell>
        <TableCell>{formattedUpdateDate}</TableCell>
        <TableCell>
          <TableCertificateAction
            setIsShowCertificateView={setIsShowCertificateView}
            setAutoOpenDownload={setAutoOpenDownload}
            certificate={certificate}
          />
        </TableCell>
      </TableRow>

      {certificate && (
        <CertificateDialog
          open={isShowCertificateView}
          onClose={handleCancelCertificateView}
          artwork={artworkWithEdition}
          certificate={certificate}
          autoOpenDownload={autoOpenDownload}
        />
      )}
    </>
  );
};

export default CertificateRow;
